import type { FC } from '../../lib/teact/teact';
import React, { useState, useEffect } from '../../lib/teact/teact';
import { getAllAccounts, getCurrentAccount, switchToAccount, clearStoredSession } from '../../util/sessions';
import { getActions } from '../../global';
import { TelegramClient } from '../../lib/gramjs';
import { StringSession } from '../../lib/gramjs/sessions';

import './AccountSwitcher.scss';

interface Account {
  userId: string;
  sessionString: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  timestamp: number;
}

const AccountSwitcher: FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [isVisible, setIsVisible] = useState<boolean>(true);

  useEffect(() => {
    // Initialize visibility state from localStorage
    const saved = localStorage.getItem('account_switcher_visible');
    if (saved !== null) {
      setIsVisible(saved === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem('account_switching');
    
    loadAccounts();
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'telegram_accounts' || e.key === 'current_account_id') {
        loadAccounts();
      }
    };
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.AccountSwitcher')) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isRefreshing]);

  const loadAccounts = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      const allAccounts = getAllAccounts();
      const current = getCurrentAccount();
      setAccounts(allAccounts);
      setCurrentAccount(current);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAccountSwitch = (userId: string) => {
    if (isLoading) return;

    if (currentAccount?.userId === userId) {
      return;
    }

    const selectedAccount = accounts.find(acc => acc.userId === userId);
    if (selectedAccount) {
    }

    localStorage.removeItem('telegram_session_string');
    localStorage.setItem('account_switching', 'true');
    
    localStorage.setItem('telegram_session_string', selectedAccount?.sessionString || '');
    localStorage.setItem('session_string_protected', 'true');
    localStorage.setItem('current_account_id', userId);
    
    if (selectedAccount) {
      try {
        const { StringSession } = require('../../lib/gramjs/sessions');
        const stringSession = new StringSession(selectedAccount.sessionString);
        stringSession.load();
        const sessionData = stringSession.getSessionData();
        
        localStorage.setItem('user_auth', JSON.stringify({
          dcID: sessionData.mainDcId,
          id: userId,
          test: sessionData.isTest,
        }));
      } catch (error) {
      }
    }

    setTimeout(() => {
      window.location.reload();
    }, 5000);
  };

  const handleAddAccount = async () => {
    if (isLoading || isRefreshing) return;

    setIsLoading(true);
    try {
      const apiId = Number(process.env.TELEGRAM_API_ID);
      const apiHash = process.env.TELEGRAM_API_HASH;

      if (!apiId || !apiHash) {
        alert('API credentials not found. Please check environment variables.');
        return;
      }

      const phoneNumber = prompt('Enter your phone number (with country code, e.g., +1234567890):');
      if (!phoneNumber) {
        return;
      }

      const { TelegramClient } = require('../../lib/gramjs');
      const { StringSession } = require('../../lib/gramjs/sessions');

      const session = new StringSession();
      const client = new TelegramClient(session, apiId, apiHash, {
        deviceModel: navigator.userAgent || 'Unknown UserAgent',
        systemVersion: navigator.platform || 'Unknown platform',
        appVersion: '1.0.0',
        useWSS: true,
        additionalDcsDisabled: false,
        shouldDebugExportedSenders: false,
        shouldForceHttpTransport: false,
        shouldAllowHttpTransport: false,
        langPack: 'en',
        langCode: 'en',
        systemLangCode: navigator.language,
        isTestServerRequested: false,
      } as any);

      await client.start({
        phoneNumber: async () => phoneNumber,
        phoneCode: async () => {
          const code = prompt('Enter the verification code sent to your phone:');
          if (!code) {
            throw new Error('Verification code required');
          }
          return code;
        },
        password: async () => {
          const password = prompt('Enter your 2FA password (if you have one, otherwise press Cancel):');
          return password || undefined;
        },
        onError: (err: any) => {
          alert(`Authentication error: ${err.message}`);
        },
      });

      const sessionString = session.save();

      const me = await client.getMe();
      const userId = me.id.toString();
      
      const firstName = me.firstName || '';
      const lastName = me.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim() || `User ${userId.slice(-4)}`;
      
      const accountData = {
        userId,
        sessionString,
        displayName: fullName,
        firstName,
        lastName,
        username: me.username || '',
        timestamp: Date.now(),
      };
      
      const storedAccounts = localStorage.getItem('telegram_accounts');
      const existingAccounts = storedAccounts ? JSON.parse(storedAccounts) : [];
      
      const accountIndex = existingAccounts.findIndex((acc: any) => acc.userId === userId);
      if (accountIndex >= 0) {
        existingAccounts[accountIndex] = accountData;
      } else {
        existingAccounts.push(accountData);
      }
      
      localStorage.setItem('telegram_accounts', JSON.stringify(existingAccounts));
      
      localStorage.removeItem('session_string_protected');
      
      const verifyAccounts = JSON.parse(localStorage.getItem('telegram_accounts') || '[]');
      
      alert(`Authentication successful!\nWelcome, ${fullName}!\nAccount saved to localStorage.\nSession string logged to console.\nLength: ${sessionString.length} characters`);
      
      await client.disconnect();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Authentication failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAccount = (userId: string) => {
    if (isLoading || isRefreshing) return;

    const accountToRemove = accounts.find(acc => acc.userId === userId);
    if (!accountToRemove) return;

    const confirmMessage = `Are you sure you want to remove account "${accountToRemove.displayName || accountToRemove.userId}"?`;
    if (!confirm(confirmMessage)) return;

    const success = switchToAccount(userId);
    if (success) {
      loadAccounts();
    }
  };

  const handleLogout = () => {
    if (isLoading || isRefreshing) return;

    if (!confirm('Are you sure you want to logout? This will clear all session data.')) {
      return;
    }

    clearStoredSession();
    window.location.reload();
  };

  const handleRefresh = async () => {
    if (isLoading || isRefreshing) return;
    await loadAccounts();
  };

  const toggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    localStorage.setItem('account_switcher_visible', newVisibility.toString());
    
    // Close dropdown when hiding
    if (!newVisibility) {
      setIsOpen(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getAccountDisplayName = (account: Account) => {
    if (account.displayName) {
      return account.displayName;
    }
    
    if (account.firstName || account.lastName) {
      const fullName = `${account.firstName || ''} ${account.lastName || ''}`.trim();
      return fullName || `User ${account.userId.slice(-4)}`;
    }
    
    return `Account ${account.userId.slice(-4)}`;
  };

  // Don't render if hidden
  if (!isVisible) {
    return (
      <div className="AccountSwitcher AccountSwitcher--hidden">
        <button
          className="AccountSwitcher__show-btn"
          onClick={toggleVisibility}
          title="Show Account Switcher"
        >
          <div className="AccountSwitcher__show-icon">👤</div>
        </button>
      </div>
    );
  }

  return (
    <div className="AccountSwitcher">
      <div 
        className="AccountSwitcher__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="AccountSwitcher__avatar">
          {currentAccount ? getAccountDisplayName(currentAccount).charAt(0).toUpperCase() : '?'}
        </div>
        <div className="AccountSwitcher__info">
          <div className="AccountSwitcher__name">
            {currentAccount ? getAccountDisplayName(currentAccount) : 'No Account'}
          </div>
          <div className="AccountSwitcher__status">
            {currentAccount ? (
              <>
                {currentAccount.username && `@${currentAccount.username} • `}
                Session: {currentAccount.sessionString.slice(0, 8)}...
              </>
            ) : (
              'Not logged in'
            )}
          </div>
        </div>
        <div className="AccountSwitcher__controls">
          <button
            className="AccountSwitcher__hide-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility();
            }}
            title="Hide Account Switcher"
          >
            ×
          </button>
          <div className="AccountSwitcher__arrow">
            ▼
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="AccountSwitcher__dropdown">
          <div className="AccountSwitcher__header">
            <div className="AccountSwitcher__header-left">
              <div className="AccountSwitcher__title">Switch Account</div>
              <div className="AccountSwitcher__count">
                {accounts.length} account{accounts.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="AccountSwitcher__header-actions">
              <button
                className="AccountSwitcher__refresh-btn"
                onClick={handleRefresh}
                disabled={isLoading || isRefreshing}
                title="Refresh accounts"
              >
                {isRefreshing ? (
                  <div className="AccountSwitcher__refresh-icon AccountSwitcher__refresh-icon--loading">⟳</div>
                ) : (
                  <div className="AccountSwitcher__refresh-icon">↻</div>
                )}
              </button>
            </div>
          </div>
          
          <div className="AccountSwitcher__list">
            {accounts.length === 0 ? (
              <div className="AccountSwitcher__empty">
                <div className="AccountSwitcher__empty-icon">👤</div>
                <div className="AccountSwitcher__empty-text">No accounts found</div>
                <div className="AccountSwitcher__empty-subtext">Add an account to get started</div>
              </div>
            ) : (
              accounts.map((account) => (
                <div
                  key={account.userId}
                  className={`AccountSwitcher__item ${currentAccount?.userId === account.userId ? 'AccountSwitcher__item--current' : ''}`}
                  onClick={() => handleAccountSwitch(account.userId)}
                >
                  <div className="AccountSwitcher__item-avatar">
                    {getAccountDisplayName(account).charAt(0).toUpperCase()}
                  </div>
                  <div className="AccountSwitcher__item-info">
                    <div className="AccountSwitcher__item-name">
                      {getAccountDisplayName(account)}
                    </div>
                    <div className="AccountSwitcher__item-details">
                      {account.username && `@${account.username} • `}
                      Session: {account.sessionString.slice(0, 12)}...
                      {account.sessionString.length > 100 && ' • Full'}
                    </div>
                    <div className="AccountSwitcher__item-time">
                      {formatTimestamp(account.timestamp)}
                    </div>
                  </div>
                  {currentAccount?.userId === account.userId && (
                    <div className="AccountSwitcher__item-indicator">
                      ✓
                    </div>
                  )}
                  <div 
                    className="AccountSwitcher__item-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAccount(account.userId);
                    }}
                    title="Remove account"
                  >
                    ×
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="AccountSwitcher__actions">
            <button 
              className="AccountSwitcher__add-btn"
              onClick={handleAddAccount}
              disabled={isLoading || isRefreshing}
            >
              <div className="AccountSwitcher__add-icon">
                {isLoading ? (
                  <div className="AccountSwitcher__add-loading">⟳</div>
                ) : (
                  '+'
                )}
              </div>
              <div className="AccountSwitcher__add-text">
                {isLoading ? 'Adding Account...' : 'Add Account'}
              </div>
            </button>
          </div>
          
          <div className="AccountSwitcher__footer">
            <div className="AccountSwitcher__help">
              Click to switch accounts or add new account
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;